/**
 * Global failure telemetry.
 *
 * Uncaught exceptions, unhandled promise rejections, and — in a code-split
 * Gatsby app, the common one — chunk-load failures otherwise fail silently:
 * the user sees a broken page and nothing is recorded. This module forwards
 * them to the GTM dataLayer as a `js_error` event so they become measurable.
 *
 * Registered once from `gatsby-browser` `onClientEntry`. It uses
 * `addEventListener` rather than assigning `window.onerror` so it never clobbers
 * another handler.
 */

const CHUNK_ERROR_PATTERNS = [
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
  'dynamically imported module',
  'import() failed',
];

/**
 * True when an error message looks like a failed JS/CSS chunk load (a stale
 * deploy or a flaky network dropping a lazily-loaded bundle), as distinct from
 * an ordinary runtime error.
 */
export function isChunkLoadError(message: string | undefined | null): boolean {
  if (!message) {
    return false;
  }
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

type ErrorKind = 'error' | 'unhandledrejection';

/**
 * Push a single failure record to the dataLayer. No-ops during SSR or before
 * the dataLayer exists so it is always safe to call.
 */
export function pushErrorEvent(kind: ErrorKind, message: string, source?: string): void {
  if (typeof window === 'undefined' || !Array.isArray(window.dataLayer)) {
    return;
  }
  window.dataLayer.push({
    event: 'js_error',
    errorKind: kind,
    errorCategory: isChunkLoadError(message) ? 'chunk' : 'runtime',
    errorMessage: message,
    ...(source ? { errorSource: source } : {}),
  });
}

const messageFromReason = (reason: unknown): string => {
  if (reason instanceof Error) {
    return reason.message;
  }
  if (typeof reason === 'string') {
    return reason;
  }
  return String(reason);
};

let registered = false;

/**
 * Register the global error / unhandledrejection listeners. Idempotent: a
 * second call is a no-op, so it is safe to invoke from every client entry.
 */
export function initErrorTelemetry(): void {
  if (typeof window === 'undefined' || registered) {
    return;
  }
  registered = true;

  window.addEventListener('error', (event: ErrorEvent) => {
    // Resource-load failures (e.g. a broken <img>) surface here with no message.
    // They are noise for this purpose — only forward errors that carry one.
    const message = event.message || (event.error instanceof Error ? event.error.message : '');
    if (!message) {
      return;
    }
    pushErrorEvent('error', message, event.filename || undefined);
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    pushErrorEvent('unhandledrejection', messageFromReason(event.reason));
  });
}
